import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';

import { getGetAccountQueryKey, getGetBillingOverviewQueryKey } from '../../../api/generated/api';

/**
 * Re-read everything that shows the balance after it changed (credits added, auto-recharge
 * toggled): the billing overview and the account, whose memberships carry the permission-free
 * credit context the drawer widget and the billing bar read.
 *
 * The app layout reads the account through `ensureQueryData` in `beforeLoad`, which returns
 * cached data even when it is stale — so the account is refetched (not just invalidated) and
 * the router re-runs its loaders to hand the fresh membership to the route context.
 */
export const useRefreshBillingContext = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useCallback(
    async (organizationId?: string) => {
      if (organizationId) {
        void queryClient.invalidateQueries({
          queryKey: getGetBillingOverviewQueryKey(organizationId),
        });
      }
      try {
        await queryClient.refetchQueries({ queryKey: getGetAccountQueryKey() });
      } catch {
        // A failed refresh leaves the previous account in place; the next navigation retries.
      }
      await router.invalidate();
    },
    [queryClient, router],
  );
};
